/**
 * FIRe spaced repetition functions for progress tracking.
 */

import type {
  WordMastery,
  MasteryLevel,
} from '../../types/progress';
import type { FIReState, EncompassingGraph } from '../../types/fire';
import { DEFAULT_FIRE_CONFIG } from '../../types/fire';
import { calculateStrengthChange } from '../mastery';
import {
  addEncounter as addEncounterToData,
  DEFAULT_ENCOUNTER_DATA,
} from '../migrateMastery';
import {
  updateFIReState,
  isDue as isFIReDue,
  getDaysOverdue as getFIReDaysOverdue,
  flowCreditDown,
  flowPenaltyUp,
  simpleToQuality as fireSimpleToQuality,
  createFIReState,
  getDecayedMemory,
} from '../fire';
import { getProgress, saveProgress } from './progressCore';
import { recordExerciseResult } from './progressLegacy';

// ---------------------------------------------------------------------------
// Encompassing graph cache
// ---------------------------------------------------------------------------

// Cached encompassing graph (built lazily)
let cachedEncompassingGraph: EncompassingGraph | null = null;

/**
 * Set the encompassing graph for FIRe credit/penalty flow.
 * Should be called once when the app initializes with lesson data.
 */
export function setEncompassingGraph(graph: EncompassingGraph): void {
  cachedEncompassingGraph = graph;
}

/**
 * Get the cached encompassing graph.
 * Returns null if not yet initialized.
 */
export function getEncompassingGraph(): EncompassingGraph | null {
  return cachedEncompassingGraph;
}

// ---------------------------------------------------------------------------
// FIRe State Accessors
// ---------------------------------------------------------------------------

/**
 * Get the FIRe state for a word, initializing if needed
 */
export function getWordFIReState(wordId: string): FIReState {
  const data = getProgress();
  const mastery = data.wordMastery[wordId];

  if (mastery?.fire) {
    return mastery.fire;
  }

  // Return default state for new words
  return createFIReState();
}

/**
 * Check if a word is due for review using FIRe algorithm
 */
export function isWordDueForFIReReview(wordId: string): boolean {
  const fire = getWordFIReState(wordId);
  return isFIReDue(fire, DEFAULT_FIRE_CONFIG);
}

/**
 * Get the number of days a word is overdue (FIRe)
 */
export function getWordFIReDaysOverdue(wordId: string): number {
  const fire = getWordFIReState(wordId);
  return getFIReDaysOverdue(fire);
}

/**
 * Get the estimated retention for a word (0-1)
 */
export function getWordRetention(wordId: string): number {
  const fire = getWordFIReState(wordId);
  return getDecayedMemory(fire);
}

/**
 * Get all words that are due for review (FIRe)
 */
export function getFIReDueWords(): string[] {
  const data = getProgress();
  const dueWords: string[] = [];

  for (const [wordId, mastery] of Object.entries(data.wordMastery)) {
    if (mastery.fire && isFIReDue(mastery.fire, DEFAULT_FIRE_CONFIG)) {
      dueWords.push(wordId);
    }
  }

  // Sort by most overdue first
  dueWords.sort((a, b) => {
    const aOverdue = getWordFIReDaysOverdue(a);
    const bOverdue = getWordFIReDaysOverdue(b);
    return bOverdue - aOverdue;
  });

  return dueWords;
}

/**
 * Get count of words due for review (FIRe)
 */
export function getFIReDueWordCount(): number {
  return getFIReDueWords().length;
}

// ---------------------------------------------------------------------------
// FIRe Recording
// ---------------------------------------------------------------------------

/**
 * Record a review result using FIRe algorithm.
 * Also handles credit/penalty flow through the encompassing graph.
 */
export function recordFIReReview(
  wordId: string,
  isCorrect: boolean,
  quality: number = isCorrect ? 1 : 0,
  encounterType: 'exercise' | 'flashcard' | 'reading' | 'listening' = 'exercise'
): void {
  const data = getProgress();
  const now = new Date().toISOString();

  // Get or create mastery entry
  const mastery: WordMastery = data.wordMastery[wordId] || {
    strength: 0,
    lastPracticed: now,
    timesCorrect: 0,
    timesIncorrect: 0,
    challengesPassed: 0,
    lastChallengeDate: null,
    fire: createFIReState(),
    encounters: { ...DEFAULT_ENCOUNTER_DATA },
  };

  // Get current FIRe state
  const currentFIRe: FIReState = mastery.fire || createFIReState();

  // Calculate new FIRe state
  const newFIRe = updateFIReState(currentFIRe, isCorrect, quality, DEFAULT_FIRE_CONFIG);
  mastery.fire = newFIRe;

  // Update encounters
  const currentEncounters = mastery.encounters || { ...DEFAULT_ENCOUNTER_DATA };
  mastery.encounters = addEncounterToData(currentEncounters, encounterType);

  // Also update legacy strength for backward compatibility
  mastery.strength = calculateStrengthChange(mastery.strength, isCorrect);
  mastery.lastPracticed = now;

  if (isCorrect) {
    mastery.timesCorrect += 1;
  } else {
    mastery.timesIncorrect += 1;
  }

  data.wordMastery[wordId] = mastery;

  // Handle credit/penalty flow through encompassing graph
  if (cachedEncompassingGraph) {
    // Create a mutable copy of fire states for flow
    const fireStates: Record<string, FIReState> = {};
    for (const [id, m] of Object.entries(data.wordMastery)) {
      if (m.fire) {
        fireStates[id] = { ...m.fire };
      }
    }

    if (isCorrect) {
      // Flow credit down to encompassed items
      flowCreditDown(wordId, 1.0, cachedEncompassingGraph, fireStates, DEFAULT_FIRE_CONFIG);
    } else {
      // Flow penalty up to encompassing items
      flowPenaltyUp(wordId, 1.0, cachedEncompassingGraph, fireStates, DEFAULT_FIRE_CONFIG);
    }

    // Apply updated fire states back to mastery
    for (const [id, fireState] of Object.entries(fireStates)) {
      if (data.wordMastery[id] && id !== wordId) {
        data.wordMastery[id].fire = fireState;
      }
    }
  }

  saveProgress(data);
}

/**
 * Record an exercise result with FIRe (simplified interface)
 */
export function recordExerciseWithFIRe(
  exerciseId: string,
  lessonId: string,
  itemIds: string[],
  isCorrect: boolean,
  wasHard: boolean = false
): void {
  // Use legacy function for exercise/lesson tracking
  recordExerciseResult(exerciseId, lessonId, itemIds, isCorrect);

  // Additionally update FIRe for each word
  const quality = fireSimpleToQuality(isCorrect, wasHard);
  const data = getProgress();
  for (const itemId of itemIds) {
    const mastery = data.wordMastery[itemId];

    if (mastery) {
      // Get current FIRe state
      const currentFIRe: FIReState = mastery.fire || createFIReState();

      // Calculate new FIRe state
      const newFIRe = updateFIReState(currentFIRe, isCorrect, quality, DEFAULT_FIRE_CONFIG);
      mastery.fire = newFIRe;

      // Update encounters
      const currentEncounters = mastery.encounters || { ...DEFAULT_ENCOUNTER_DATA };
      mastery.encounters = addEncounterToData(currentEncounters, 'exercise');

      data.wordMastery[itemId] = mastery;
    }
  }
  saveProgress(data);

  // Handle credit/penalty flow through encompassing graph
  if (cachedEncompassingGraph) {

    // Create a mutable copy of fire states for flow
    const fireStates: Record<string, FIReState> = {};
    for (const [id, m] of Object.entries(data.wordMastery)) {
      if (m.fire) {
        fireStates[id] = { ...m.fire };
      }
    }

    // Flow credit/penalty for each item in the exercise
    for (const itemId of itemIds) {
      if (isCorrect) {
        flowCreditDown(itemId, 1.0, cachedEncompassingGraph, fireStates, DEFAULT_FIRE_CONFIG);
      } else {
        flowPenaltyUp(itemId, 1.0, cachedEncompassingGraph, fireStates, DEFAULT_FIRE_CONFIG);
      }
    }

    // Apply updated fire states back to mastery
    for (const [id, fireState] of Object.entries(fireStates)) {
      if (data.wordMastery[id] && !itemIds.includes(id)) {
        data.wordMastery[id].fire = fireState;
      }
    }

    saveProgress(data);
  }
}

// ---------------------------------------------------------------------------
// FIRe Statistics
// ---------------------------------------------------------------------------

/**
 * Get review statistics using FIRe algorithm
 */
export function getFIReReviewStats(): {
  dueToday: number;
  overdueCount: number;
  reviewedToday: number;
  upcomingWeek: number;
} {
  const data = getProgress();
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const todayEnd = new Date();
  todayEnd.setHours(23, 59, 59, 999);

  let dueToday = 0;
  let overdueCount = 0;
  let reviewedToday = 0;
  const upcomingWeek = 0; // FIRe doesn't have fixed next review dates

  for (const mastery of Object.values(data.wordMastery)) {
    if (!mastery.fire) continue;

    // Check if due using FIRe
    if (isFIReDue(mastery.fire, DEFAULT_FIRE_CONFIG)) {
      dueToday++;
      if (getFIReDaysOverdue(mastery.fire) > 0) {
        overdueCount++;
      }
    }

    // Check if reviewed today (by looking at lastPracticed)
    const lastPracticed = new Date(mastery.lastPracticed).getTime();
    if (lastPracticed >= todayStart.getTime() && lastPracticed <= todayEnd.getTime()) {
      reviewedToday++;
    }
  }

  return {
    dueToday,
    overdueCount,
    reviewedToday,
    upcomingWeek,
  };
}

/**
 * Get mastery level for a word using FIRe state
 */
export function getWordMasteryLevelFIRe(wordId: string): MasteryLevel {
  const data = getProgress();
  const mastery = data.wordMastery[wordId];

  if (!mastery?.fire || mastery.fire.repNum === 0) {
    return 'new';
  }

  const fire = mastery.fire;
  const memory = getDecayedMemory(fire);

  // Check for decay (low memory)
  if (memory < 0.3 && fire.repNum >= 1) {
    return 'decaying';
  }

  // Based on repNum thresholds
  if (fire.repNum >= 3) {
    return 'mastered';
  }
  if (fire.repNum >= 1.5) {
    return 'familiar';
  }
  return 'learning';
}
